import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import dynamic from "next/dynamic";
import classNames from "classnames";
import { formatToPhone } from "@src/utils/utils";
import { showEnterForm } from "@app/store/layoutSlice";
import { AUTH_FORM_TEXT, CONTACT_TEXT, CONTACT_TIMEOUT } from "./constants";
import { ContactButtonProps } from "./types";
import classes from "./contact-button.module.scss";

const MotionDiv = dynamic(() => import("framer-motion").then((mod) => mod.motion.div), {
    ssr: false
});

export const ContactButton = ({
    phone,
    name,
    isAuth = false,
    className,
    phoneClassName,
    labelClassName
}: ContactButtonProps) => {
    const [isPhoneLoading, setIsPhoneLoading] = useState(false);
    const [isPhoneVisible, setIsPhoneVisible] = useState(false);
    const phoneRef = useRef<HTMLAnchorElement>(null);

    const dispatch = useDispatch();
    const formattedPhone = phone ? formatToPhone(phone) : CONTACT_TEXT.PHONE_UNKNOWN;
    const maskedPhone = phone
        ? formattedPhone.slice(0, -7) + "X-XX-XX"
        : CONTACT_TEXT.PHONE_UNKNOWN;
    const formattedName = name ?? "";

    const phoneButtonVariants = {
        hidden: {
            display: "none",
            opacity: 0,
            scale: 0
        },
        visible: {
            display: "block",
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.2
            }
        }
    };

    const handleShowPhoneClick = () => {
        if (!isAuth) {
            dispatch(showEnterForm(AUTH_FORM_TEXT));
            return;
        }

        if (isPhoneVisible && phoneRef.current) {
            phoneRef.current.click();
            return;
        }

        setIsPhoneLoading(true);
        setTimeout(() => {
            setIsPhoneVisible(true);
            setIsPhoneLoading(false);
        }, CONTACT_TIMEOUT);
    };

    return (
        <button
            className={classNames(classes["contact-button"], className, {
                [classes["contact-button__loading"]]: isPhoneLoading
            })}
            aria-label={CONTACT_TEXT.SHOW_PHONE}
            onClick={handleShowPhoneClick}
            disabled={isPhoneLoading}
        >
            {isPhoneLoading && <div className={classes["contact-button__loader"]} />}
            <div className={classes["contact-button__content"]}>
                <MotionDiv
                    initial="hidden"
                    variants={phoneButtonVariants}
                    animate={isPhoneVisible ? "visible" : "hidden"}
                >
                    <a
                        className={classNames(classes["contact-button__phone"], phoneClassName)}
                        ref={phoneRef}
                        href={"tel:" + formattedPhone}
                    >
                        {formattedPhone}
                    </a>
                    {formattedName && (
                        <div
                            className={classNames(classes["contact-button__label"], labelClassName)}
                        >
                            {formattedName}
                        </div>
                    )}
                </MotionDiv>
                <MotionDiv
                    variants={phoneButtonVariants}
                    animate={isPhoneVisible ? "hidden" : "visible"}
                >
                    <div className={classNames(classes["contact-button__phone"], phoneClassName)}>
                        {maskedPhone}
                    </div>
                    <div className={classNames(classes["contact-button__label"], labelClassName)}>
                        {CONTACT_TEXT.SHOW_PHONE}
                    </div>
                </MotionDiv>
            </div>
        </button>
    );
};